import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { History, User, Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  name: 'Name',
  price: 'Price',
  original_price: 'Compare-at price',
  stock: 'Stock',
  shipping_fee: 'Shipping fee',
  category: 'Category',
  brand: 'Brand',
  description: 'Description',
  sku: 'SKU',
  image_url: 'Primary image',
  images: 'Images',
  active: 'Active',
  is_top_selling: 'Top-selling',
  is_pre_order: 'Pre-order',
  group_name: 'Group',
  variant_attributes: 'Variant attributes',
  product_code: 'Product code',
};

const ACTION_META = {
  created: { label: 'Created', icon: Plus, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  updated: { label: 'Updated', icon: Pencil, color: 'text-blue-600', bg: 'bg-blue-50' },
  activated: { label: 'Activated', icon: Eye, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  deactivated: { label: 'Deactivated', icon: EyeOff, color: 'text-amber-600', bg: 'bg-amber-50' },
  deleted: { label: 'Deleted', icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
};

const formatVal = (val, field) => {
  if (val === null || val === undefined || val === '') return '—';
  if (field === 'price' || field === 'original_price' || field === 'shipping_fee') {
    return `₹${Number(val).toLocaleString('en-IN')}`;
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return val.length ? `${val.length} item${val.length > 1 ? 's' : ''}` : '—';
  if (typeof val === 'object') {
    const entries = Object.entries(val);
    if (!entries.length) return '—';
    return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
  }
  const s = String(val);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 30 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};

const ProductHistoryModal = ({ open, onOpenChange, product }) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !product?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('admin_token');
        const res = await axios.get(`${API}/products/${product.id}/audit-logs?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setLogs(res.data?.logs || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Could not load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, product?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="product-history-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#0047AB]" />
            <DialogTitle>
              History · <span className="font-normal text-slate-500">{product?.name}</span>
            </DialogTitle>
          </div>
          {product?.product_code && (
            <p className="text-xs text-slate-400">Product code: {product.product_code}</p>
          )}
        </DialogHeader>

        <div className="mt-2 flex-1 overflow-y-auto -mx-6 px-6" data-testid="product-history-body">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">Loading history…</div>
          ) : error ? (
            <div className="py-10 text-center text-sm text-red-600">{error}</div>
          ) : logs.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">No changes recorded yet.</div>
          ) : (
            <ol className="relative border-l-2 border-slate-100 pl-4 py-2 space-y-3">
              {logs.map((entry) => {
                const meta = ACTION_META[entry.action] || ACTION_META.updated;
                const Icon = meta.icon;
                const changeEntries = Object.entries(entry.changes || {});
                return (
                  <li key={entry.id} className="relative" data-testid={`history-entry-${entry.id}`}>
                    <span className={`absolute -left-[22px] top-1 w-6 h-6 rounded-full ${meta.bg} flex items-center justify-center`}>
                      <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                    </span>
                    <div className="bg-white border border-slate-100 rounded-lg p-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`font-semibold ${meta.color}`}>{meta.label}</span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {entry.actor}
                            {entry.actor_role === 'super_admin' && (
                              <span className="ml-1 text-[10px] uppercase tracking-wider bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">
                                super
                              </span>
                            )}
                          </span>
                        </div>
                        <span className="text-xs text-slate-400" title={entry.timestamp}>
                          {relativeTime(entry.timestamp)}
                        </span>
                      </div>

                      {changeEntries.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {changeEntries.map(([field, val]) => {
                            const label = FIELD_LABELS[field] || field;
                            // For created/deleted, val is scalar (not {old,new})
                            if (val && typeof val === 'object' && 'old' in val && 'new' in val) {
                              return (
                                <li key={field} className="text-xs text-slate-600">
                                  <span className="font-medium text-slate-700">{label}:</span>{' '}
                                  <span className="text-slate-500 line-through">{formatVal(val.old, field)}</span>
                                  {' → '}
                                  <span className="text-slate-900 font-medium">{formatVal(val.new, field)}</span>
                                </li>
                              );
                            }
                            return (
                              <li key={field} className="text-xs text-slate-600">
                                <span className="font-medium text-slate-700">{label}:</span>{' '}
                                <span className="text-slate-900">{formatVal(val, field)}</span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductHistoryModal;
